import { useState, useEffect, useRef } from 'react';
import { Coffee, Check, Copy, Share2, Send, ArrowRight, X } from 'lucide-react';
import {
  RELATIONSHIPS,
  TIME_SINCE_OPTIONS,
  createSubmission,
  type NewSubmission,
  type CoffeeSubmission,
} from '../../lib/coffee-project';

interface Props {
  onSubmitted: (s: CoffeeSubmission) => void;
}

const SUGGESTED_TEXT = (theirName: string) =>
  `Apparently I just submitted us to a project called "The Coffee You Keep Meaning to Have," which feels like a pretty clear sign that we should finally do this. Coffee?`;

export default function SubmissionForm({ onSubmitted }: Props) {
  const [step, setStep] = useState<'form' | 'submitting' | 'error'>('form');
  const [errorMsg, setErrorMsg] = useState('');
  const [yourName, setYourName] = useState('');
  const [theirName, setTheirName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [timeSince, setTimeSince] = useState('');
  const [reason, setReason] = useState('');
  const [talkAbout, setTalkAbout] = useState('');
  const [wantsHelp, setWantsHelp] = useState<boolean | null>(null);
  const [contactInfo, setContactInfo] = useState('');
  const [permission, setPermission] = useState<'anonymous' | 'first_names' | 'private'>('anonymous');
  const formRef = useRef<HTMLDivElement>(null);

  const canSubmit = yourName.trim() && theirName.trim() && relationship && timeSince && wantsHelp !== null && permission;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setStep('submitting');
    setErrorMsg('');
    try {
      const input: NewSubmission = {
        submitter_name: yourName.trim(),
        other_name: theirName.trim(),
        relationship,
        time_since: timeSince,
        reason: reason.trim() || null,
        talk_about: talkAbout.trim() || null,
        contact_info: wantsHelp ? contactInfo.trim() : null,
        wants_help: wantsHelp ?? false,
        permission_level: permission,
      };
      const result = await createSubmission(input);
      onSubmitted(result);
    } catch (err) {
      setStep('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    }
  };

  const inputClass = 'w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:border-[#fda36b] focus:outline-none transition-colors';

  return (
    <div ref={formRef} className="max-w-2xl mx-auto">
      {step === 'form' && (
        <div className="space-y-8">
          <div className="text-center space-y-2">
            <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">Okay. Who is it?</h2>
            <p className="text-white/60 text-lg">No accounts. No pressure. Just tell us about the coffee.</p>
          </div>

          <div className="space-y-6 bg-white/[0.03] border border-white/10 rounded-2xl p-6 md:p-10">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Your first name</label>
                <input className={inputClass} value={yourName} onChange={(e) => setYourName(e.target.value)} placeholder="Chris" />
              </div>
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">Their first name</label>
                <input className={inputClass} value={theirName} onChange={(e) => setTheirName(e.target.value)} placeholder="Dave" />
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-3">What are they to you?</label>
              <div className="flex flex-wrap gap-2">
                {RELATIONSHIPS.map((r) => (
                  <button
                    key={r}
                    onClick={() => setRelationship(r)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      relationship === r
                        ? 'bg-[#fda36b] text-[#132947] border-[#fda36b]'
                        : 'bg-white/5 text-white/70 border border-white/15 hover:border-[#fda36b]/50 hover:text-white'
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-3">How long has it been?</label>
              <div className="flex flex-wrap gap-2">
                {TIME_SINCE_OPTIONS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTimeSince(t)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      timeSince === t
                        ? 'bg-[#fda36b] text-[#132947] border-[#fda36b]'
                        : 'bg-white/5 text-white/70 border border-white/15 hover:border-[#fda36b]/50 hover:text-white'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">Why haven't you had the coffee yet? <span className="text-white/40">(optional)</span></label>
              <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="He lives 20 minutes away. There's really no excuse." />
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-2">What would you want to talk about? <span className="text-white/40">(optional)</span></label>
              <textarea className={`${inputClass} min-h-[100px] resize-none`} value={talkAbout} onChange={(e) => setTalkAbout(e.target.value)} placeholder="Everything and nothing." />
            </div>

            <div>
              <label className="block text-white/70 text-sm font-medium mb-3">Should Bond try to help make this coffee happen?</label>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setWantsHelp(true)}
                  className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                    wantsHelp === true
                      ? 'bg-[#fda36b] text-[#132947] border-[#fda36b]'
                      : 'bg-white/5 text-white/70 border border-white/15 hover:border-[#fda36b]/50'
                  }`}
                >
                  Yes — reach out to me
                </button>
                <button
                  onClick={() => setWantsHelp(false)}
                  className={`flex-1 px-5 py-3 rounded-xl text-sm font-semibold transition-all ${
                    wantsHelp === false
                      ? 'bg-white/15 text-white border-white/30'
                      : 'bg-white/5 text-white/70 border border-white/15 hover:border-white/30'
                  }`}
                >
                  No — just add it to the experiment
                </button>
              </div>
            </div>

            {wantsHelp && (
              <div className="animate-[fadeIn_0.3s_ease]">
                <label className="block text-white/70 text-sm font-medium mb-2">Email or mobile number</label>
                <input className={inputClass} value={contactInfo} onChange={(e) => setContactInfo(e.target.value)} placeholder="you@example.com or 555-0100" />
                <p className="text-white/40 text-xs mt-2">We'll only use this to contact you about this project.</p>
              </div>
            )}

            <div>
              <label className="block text-white/70 text-sm font-medium mb-3">Can we anonymously share your story on the project page?</label>
              <div className="flex flex-col gap-2">
                {([
                  { v: 'anonymous', label: 'Yes, anonymously' },
                  { v: 'first_names', label: 'Yes, you can use our first names' },
                  { v: 'private', label: 'No, keep it private' },
                ] as const).map((opt) => (
                  <button
                    key={opt.v}
                    onClick={() => setPermission(opt.v)}
                    className={`px-5 py-3 rounded-xl text-sm font-medium text-left transition-all ${
                      permission === opt.v
                        ? 'bg-[#fda36b]/15 text-[#fda36b] border-[#fda36b]/50'
                        : 'bg-white/5 text-white/70 border border-white/15 hover:border-white/30'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
              <p className="text-white/50 text-xs leading-relaxed">
                You control what gets shared. We will never publish your email, phone number, or private contact information.
              </p>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`w-full py-5 rounded-xl font-bold text-lg transition-all ${
                canSubmit
                  ? 'bg-[#fda36b] text-[#132947] hover:bg-[#fda36b]/90 hover:scale-[1.02] shadow-lg shadow-[#fda36b]/20'
                  : 'bg-white/5 text-white/30 cursor-not-allowed'
              }`}
            >
              Put Us On The Board
            </button>
          </div>
        </div>
      )}

      {step === 'submitting' && (
        <div className="text-center py-20 space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#fda36b]/20 animate-pulse">
            <Coffee className="w-8 h-8 text-[#fda36b] animate-bounce" />
          </div>
          <p className="text-white/70 text-lg">Adding your coffee to the board...</p>
        </div>
      )}

      {step === 'error' && (
        <div className="text-center py-20 space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-500/20">
            <X className="w-8 h-8 text-rose-400" />
          </div>
          <p className="text-rose-400 text-lg">{errorMsg}</p>
          <button onClick={() => setStep('form')} className="text-[#fda36b] hover:underline">Try again</button>
        </div>
      )}
    </div>
  );
}

export function SubmissionSuccess({ submission, onTellUs }: { submission: CoffeeSubmission; onTellUs: () => void }) {
  const [copied, setCopied] = useState(false);
  const text = SUGGESTED_TEXT(submission.other_name);
  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/coffee` : '';

  const copyText = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ text, url: pageUrl, title: 'The Coffee You Keep Meaning to Have' });
      } catch {
        // user cancelled
      }
    } else {
      copyText();
    }
  };

  return (
    <div className="max-w-2xl mx-auto text-center space-y-8 animate-[fadeIn_0.5s_ease]">
      <div className="space-y-4">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20">
          <Check className="w-10 h-10 text-emerald-400" />
        </div>
        <p className="text-[#fda36b] text-sm font-semibold uppercase tracking-wider">Coffee #{submission.project_number}</p>
        <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight">
          {submission.permission_level === 'anonymous' ? 'Anonymous + Anonymous' : `${submission.submitter_name} + ${submission.other_name}`}
        </h2>
        <p className="text-white/70 text-lg">You're officially part of the experiment.</p>
      </div>

      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 md:p-8 space-y-6">
        <div>
          <p className="text-white/80 text-lg leading-relaxed">Now comes the difficult part:</p>
          <p className="text-2xl font-bold text-[#fda36b] mt-2">Actually text {submission.other_name}.</p>
        </div>

        <div className="bg-[#132947] border border-white/10 rounded-xl p-5 text-left">
          <p className="text-white/90 leading-relaxed italic">"{text}"</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={nativeShare}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#fda36b] text-[#132947] py-3 rounded-xl font-bold hover:bg-[#fda36b]/90 transition-all"
          >
            <Send className="w-5 h-5" /> Send Them This
          </button>
          <button
            onClick={copyText}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white/10 text-white border border-white/15 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all"
          >
            {copied ? <Check className="w-5 h-5 text-emerald-400" /> : <Copy className="w-5 h-5" />}
            {copied ? 'Copied!' : 'Copy Text'}
          </button>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 pt-2">
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 text-white/70 border border-white/10 py-3 rounded-xl text-sm font-medium hover:bg-white/10 transition-all"
          >
            <Share2 className="w-4 h-4" /> Share to Facebook
          </a>
          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent('Who have you been meaning to have coffee with?')}&url=${encodeURIComponent(pageUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-white/5 text-white/70 border border-white/10 py-3 rounded-xl text-sm font-medium hover:bg-white/10 transition-all"
          >
            <Share2 className="w-4 h-4" /> Share to Twitter
          </a>
        </div>
      </div>

      <button
        onClick={onTellUs}
        className="inline-flex items-center gap-2 text-[#fda36b] hover:gap-3 transition-all text-lg font-semibold"
      >
        Tell us when it happens <ArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}
